<template>
    <div class="h-full w-full flex flex-col">
        <div class="h-full container mx-auto">
            <div class="px-8 flex flex-col h-full w-full py-8">
                <h1 class="text-center text-3xl font-bold">Security Clearance Form</h1>
                <div v-if="eventLoading" class="flex justify-center items-center py-16">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <div v-else-if="eventError"
                    class="mt-8 bg-red-50 border border-red-200 rounded-2xl p-8 text-center shadow-sm">
                    <h2 class="text-2xl font-semibold text-red-700 mb-3">Link not available</h2>
                    <p class="text-gray-700 mb-4">{{ eventError }}</p>
                    <p class="text-sm text-gray-500">If you believe this is an error, please contact the event
                        coordinator to request a new link.</p>
                </div>
                <div v-else class="flex flex-col mt-8">
                    <div class="flex flex-col border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Event Details</h2>
                        <div class="p-8 bg-white">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Event</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.event : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Purpose of the Event</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.purpose_of_event : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Speaker / Representative
                                    </p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.speaker : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Start Date</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ formatEventDateTime(eventDetails ?
                                        eventDetails.date_time : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">End Date</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ formatEventDateTime(eventDetails ?
                                        eventDetails.end_date : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Location</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.entity_location : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Target Group</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.target_group : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Age Group</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.age_group : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Requester</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.requester : null) }}</p>
                                </div>
                                <div>
                                    <p class="text-xs uppercase tracking-widest text-gray-500">Supervisor</p>
                                    <p class="text-lg font-semibold text-gray-900">{{ displayValue(eventDetails ?
                                        eventDetails.supervisor : null) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Basic Information</h2>
                        <div class="p-8">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !applicationType.isValid }">
                                    <label for="applicationType">Application Type*</label>
                                    <select class="mt-2 ht-42" v-model.trim="applicationType.val"
                                        @blur="clearValidity('applicationType')" id="applicationType">
                                        <option value="">Select an option</option>
                                        <option value="Individual">Individual</option>
                                        <option value="Company">Company</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!applicationType.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ applicationType.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !purposeOfRequest.isValid }">
                                    <label for="purposeOfRequest">Purpose of Request*</label>
                                    <select class="mt-2 ht-42" v-model.trim="purposeOfRequest.val"
                                        @blur="clearValidity('purposeOfRequest')" id="purposeOfRequest">
                                        <option value="">Select an option</option>
                                        <option v-for="(item, index) in getPurposeOfRequest" :key="index" :value="item">
                                            {{ item }}</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!purposeOfRequest.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ purposeOfRequest.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <transition name="fade" mode="out-in">
                                    <div v-if="displayOtherPurposeOfRequest" class="">
                                        <label for="otherPurposeOfRequest">Enter Purpose of Request*</label>
                                        <input class="mt-2" type="text" v-model.trim="otherPurposeOfRequest.val"
                                            @blur="clearValidity('otherPurposeOfRequest')" id="otherPurposeOfRequest">
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!otherPurposeOfRequest.isValid"
                                                class="h-4 text-xs text-red-600 ml-1">
                                                {{ otherPurposeOfRequest.msg }}
                                            </p>
                                        </transition>
                                    </div>
                                </transition>
                            </div>
                            <div class="grid grid-cols-3 gap-6 mt-6">
                                <div class="flex flex-col" :class="{ invalid: !entity.isValid }">
                                    <label for="entity">Entity*</label>
                                    <select class="mt-2 ht-42" v-model.trim="entity.val" @blur="clearValidity('entity')"
                                        id="entity">
                                        <option value="">Select an entity</option>
                                        <option value="ADPOLY">ADPOLY</option>
                                        <option value="ADVETI">ADVETI</option>
                                        <option value="ATHS">ATS</option>
                                        <option value="FCHS">FCHS</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!entity.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ entity.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Personal Information</h2>
                        <div class="p-8">
                            <div class="flex">
                                <div class="flex flex-col">
                                    <div v-if="profilePicturePreview" class="relative">
                                        <div @click.stop="cancelUploadedPhoto"
                                            class="absolute top-0 left-0 cursor-pointer h-6 w-6 border border-red-600 rounded-full flex flex-col items-center justify-center">
                                            <icon-close class="h-4 w-4 text-red-600"></icon-close>
                                        </div>
                                        <img class="h-48 object-contain" :src="profilePicturePreview">
                                    </div>
                                    <img v-else class="h-48" src="../../assets/images/default/avatar.jpg">
                                    <div class="flex justify-start mt-4">
                                        <input ref="profilePicture" class="hidden" id="profile-picture"
                                            @change="handleProfilePicture" type="file" accept="image/*">
                                        <div @click.stop="onPickFile('profilePicture')"
                                            class="text-twelve flex items-center cursor-pointer">
                                            <icon-plus class="h-4 w-4"></icon-plus>
                                            <span class="ml-1">Upload Photo*</span>
                                        </div>
                                    </div>
                                    <transition name="fade" mode="out-in">
                                        <p v-if="!profilePicture.isValid" class="h-4 text-xs text-red-600">
                                            {{ profilePicture.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                            <div class="grid grid-cols-3 gap-6 mt-6">
                                <div class="flex flex-col" :class="{ invalid: !fullName.isValid }">
                                    <label for="fullName">Full Name <span class="font-light text-sm">(as in
                                            passport)</span>*</label>
                                    <input class="mt-2" type="text" v-model.trim="fullName.val"
                                        @blur="clearValidity('fullName')" id="fullName">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fullName.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fullName.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !dateOfBirth.isValid }">
                                    <label for="dateOfBirth">Date of Birth*</label>
                                    <input class="mt-2" type="date" v-model.trim="dateOfBirth.val"
                                        @blur="clearValidity('dateOfBirth')" id="dateOfBirth">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!dateOfBirth.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ dateOfBirth.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !placeOfBirth.isValid }">
                                    <label for="placeOfBirth">Place of Birth*</label>
                                    <input class="mt-2" type="text" v-model.trim="placeOfBirth.val"
                                        @blur="clearValidity('placeOfBirth')" id="placeOfBirth">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!placeOfBirth.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ placeOfBirth.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !currentNationality.isValid }">
                                    <label for="currentNationality">Current Nationality*</label>
                                    <select class="mt-2 ht-42" v-model.trim="currentNationality.val"
                                        @blur="clearValidity('currentNationality')" id="currentNationality">
                                        <option value="">Select an option</option>
                                        <option v-for="(country, index) in countries" :key="index"
                                            :value="country.country_name">{{ country.country_name }} </option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!currentNationality.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ currentNationality.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !previousNationality.isValid }">
                                    <label for="previousNationality">Previous Nationality*</label>
                                    <select class="mt-2 ht-42" v-model.trim="previousNationality.val"
                                        @blur="clearValidity('previousNationality')" id="previousNationality">
                                        <option value="">Select an option</option>
                                        <option v-for="(country, index) in countries" :key="index"
                                            :value="country.country_name">{{ country.country_name }} </option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!previousNationality.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ previousNationality.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !passportNumber.isValid }">
                                    <label for="passportNumber">Passport Number*</label>
                                    <input class="mt-2" type="text" v-model.trim="passportNumber.val"
                                        @blur="clearValidity('passportNumber')" id="passportNumber">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!passportNumber.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ passportNumber.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !placeOfIssue.isValid }">
                                    <label for="placeOfIssue">Place of Issue*</label>
                                    <input class="mt-2" type="text" v-model.trim="placeOfIssue.val"
                                        @blur="clearValidity('placeOfIssue')" id="placeOfIssue">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!placeOfIssue.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ placeOfIssue.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !dateOfIssue.isValid }">
                                    <label for="dateOfIssue">Date of Issue*</label>
                                    <input class="mt-2" type="date" v-model.trim="dateOfIssue.val"
                                        @blur="clearValidity('dateOfIssue')" id="dateOfIssue">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!dateOfIssue.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ dateOfIssue.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !dateOfExpiry.isValid }">
                                    <label for="dateOfExpiry">Date of Expiry*</label>
                                    <input class="mt-2" type="date" v-model.trim="dateOfExpiry.val"
                                        @blur="clearValidity('dateOfExpiry')" id="dateOfExpiry">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!dateOfExpiry.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ dateOfExpiry.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !maritalStatus.isValid }">
                                    <label for="maritalStatus">Marital Status*</label>
                                    <select class="mt-2 ht-42" v-model.trim="maritalStatus.val"
                                        @blur="clearValidity('maritalStatus')" id="maritalStatus">
                                        <option value="">Select an option</option>
                                        <option value="Single">Single</option>
                                        <option value="Married">Married</option>
                                        <option value="Divorced">Divorced</option>
                                        <option value="Widowed">Widowed</option>
                                        <option value="Separated">Separated</option>
                                        <option value="Prefer not to say">Prefer not to say</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!maritalStatus.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ maritalStatus.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !religion.isValid }">
                                    <label for="religion">Religion*</label>
                                    <select class="mt-2 ht-42" v-model.trim="religion.val"
                                        @blur="clearValidity('religion')" id="religion">
                                        <option value="">Select an option</option>
                                        <option value="Islam">Islam</option>
                                        <option value="Christianity">Christianity</option>
                                        <option value="Judaism">Judaism</option>
                                        <option value="Hinduism">Hinduism</option>
                                        <option value="buddhism">Buddhism</option>
                                        <option value="Sikhism">Sikhism</option>
                                        <option value="Bahá'í Faith">Bahá'í Faith</option>
                                        <option value="Jainism">Jainism</option>
                                        <option value="Shinto">Shinto</option>
                                        <option value="Taoism">Taoism</option>
                                        <option value="Zoroastrianism">Zoroastrianism</option>
                                        <option value="Confucianism">Confucianism</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!religion.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ religion.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <transition name="fade" mode="out-in">
                                    <div v-if="displayOtherReligion" class="flex flex-col"
                                        :class="{ invalid: !otherReligion.isValid }">
                                        <label for="otherReligion">Enter your religion*</label>
                                        <input class="mt-2" type="text" v-model.trim="sect.val"
                                            @blur="clearValidity('otherReligion')" id="otherReligion">
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!otherReligion.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ otherReligion.msg }}
                                            </p>
                                        </transition>
                                    </div>
                                </transition>
                                <div class="flex flex-col" :class="{ invalid: !sect.isValid }">
                                    <label for="sect">Sect*</label>
                                    <input class="mt-2" type="text" v-model.trim="sect.val"
                                        @blur="clearValidity('sect')" id="sect">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!sect.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ sect.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherTongue.isValid }">
                                    <label for="motherTongue">Mother Tongue*</label>
                                    <input class="mt-2" type="text" v-model.trim="motherTongue.val"
                                        @blur="clearValidity('motherTongue')" id="motherTongue">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherTongue.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherTongue.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !otherLanguagesKnown.isValid }">
                                    <label for="otherLanguagesKnown">Other Languages Known</label>
                                    <input class="mt-2" type="text" v-model.trim="otherLanguagesKnown.val"
                                        @blur="clearValidity('otherLanguagesKnown')" id="otherLanguagesKnown">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!otherLanguagesKnown.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ otherLanguagesKnown.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Parent's Information</h2>
                        <div class="p-8">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !fatherName.isValid }">
                                    <label for="fatherName">Father's Name*</label>
                                    <input class="mt-2" type="text" v-model.trim="fatherName.val"
                                        @blur="clearValidity('fatherName')" id="fatherName">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fatherName.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fatherName.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !fatherNationality.isValid }">
                                    <label for="fatherNationality">Nationality*</label>
                                    <select class="mt-2 ht-42" v-model.trim="fatherNationality.val"
                                        @blur="clearValidity('fatherNationality')" id="fatherNationality">
                                        <option value="">Select an option</option>
                                        <option v-for="(country, index) in countries" :key="index"
                                            :value="country.country_name">{{ country.country_name }} </option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fatherNationality.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fatherNationality.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !fatherProfession.isValid }">
                                    <label for="fatherProfession">Profession*</label>
                                    <input class="mt-2" type="text" v-model.trim="fatherProfession.val"
                                        @blur="clearValidity('fatherProfession')" id="fatherProfession">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fatherProfession.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fatherProfession.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !fatherEmployer.isValid }">
                                    <label for="fatherEmployer">Employer</label>
                                    <input class="mt-2" type="text" v-model.trim="fatherEmployer.val"
                                        @blur="clearValidity('fatherEmployer')" id="fatherEmployer">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fatherEmployer.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fatherEmployer.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !fatherDateOfBirth.isValid }">
                                    <label for="fatherDateOfBirth">Date of Birth*</label>
                                    <input class="mt-2" type="date" v-model.trim="fatherDateOfBirth.val"
                                        @blur="clearValidity('fatherDateOfBirth')" id="fatherDateOfBirth">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fatherDateOfBirth.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fatherDateOfBirth.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !fatherPlaceOfBirth.isValid }">
                                    <label for="fatherPlaceOfBirth">Place of Birth*</label>
                                    <input class="mt-2" type="text" v-model.trim="fatherPlaceOfBirth.val"
                                        @blur="clearValidity('fatherPlaceOfBirth')" id="fatherPlaceOfBirth">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!fatherPlaceOfBirth.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ fatherPlaceOfBirth.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherName.isValid }">
                                    <label for="motherName">Mother's Name*</label>
                                    <input class="mt-2" type="text" v-model.trim="motherName.val"
                                        @blur="clearValidity('motherName')" id="motherName">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherName.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherName.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherNationality.isValid }">
                                    <label for="motherNationality">Nationality*</label>
                                    <select class="mt-2 ht-42" v-model.trim="motherNationality.val"
                                        @blur="clearValidity('motherNationality')" id="motherNationality">
                                        <option value="">Select an option</option>
                                        <option v-for="(country, index) in countries" :key="index"
                                            :value="country.country_name">{{ country.country_name }} </option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherNationality.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherNationality.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherProfession.isValid }">
                                    <label for="motherProfession">Profession*</label>
                                    <input class="mt-2" type="text" v-model.trim="motherProfession.val"
                                        @blur="clearValidity('motherProfession')" id="motherProfession">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherProfession.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherProfession.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherEmployer.isValid }">
                                    <label for="motherEmployer">Employer</label>
                                    <input class="mt-2" type="text" v-model.trim="motherEmployer.val"
                                        @blur="clearValidity('motherEmployer')" id="motherEmployer">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherEmployer.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherEmployer.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherDateOfBirth.isValid }">
                                    <label for="motherDateOfBirth">Date of Birth*</label>
                                    <input class="mt-2" type="date" v-model.trim="motherDateOfBirth.val"
                                        @blur="clearValidity('motherDateOfBirth')" id="motherDateOfBirth">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherDateOfBirth.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherDateOfBirth.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !motherPlaceOfBirth.isValid }">
                                    <label for="motherPlaceOfBirth">Place of Birth*</label>
                                    <input class="mt-2" type="text" v-model.trim="motherPlaceOfBirth.val"
                                        @blur="clearValidity('motherPlaceOfBirth')" id="motherPlaceOfBirth">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!motherPlaceOfBirth.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ motherPlaceOfBirth.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Academic Qualifications</h2>
                        <div class="p-8">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !highestQualification.isValid }">
                                    <label for="highestQualification">Highest Qualification*</label>
                                    <input class="mt-2" type="text" v-model.trim="highestQualification.val"
                                        @blur="clearValidity('highestQualification')" id="highestQualification">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!highestQualification.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ highestQualification.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !major.isValid }">
                                    <label for="major">Major*</label>
                                    <input class="mt-2" type="text" v-model.trim="major.val"
                                        @blur="clearValidity('major')" id="major">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!major.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ major.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !institution.isValid }">
                                    <label for="institution">Institution*</label>
                                    <input class="mt-2" type="text" v-model.trim="institution.val"
                                        @blur="clearValidity('institution')" id="institution">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!institution.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ institution.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !academicQualificationCountry.isValid }">
                                    <label for="academicQualificationCountry">Country*</label>
                                    <select class="mt-2 ht-42" v-model.trim="academicQualificationCountry.val"
                                        @blur="clearValidity('academicQualificationCountry')"
                                        id="academicQualificationCountry">
                                        <option value="">Select an option</option>
                                        <option v-for="(country, index) in countries" :key="index"
                                            :value="country.country_name">{{ country.country_name }} </option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!academicQualificationCountry.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ academicQualificationCountry.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !academicQualificationYear.isValid }">
                                    <label for="academicQualificationYear">Year*</label>
                                    <input class="mt-2" type="text" v-model.trim="academicQualificationYear.val"
                                        @blur="clearValidity('academicQualificationYear')"
                                        id="academicQualificationYear">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!academicQualificationYear.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ academicQualificationYear.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Military Service</h2>
                        <div class="p-8">
                            <div class="grid grid-cols-1 gap-6">
                                <div class="flex flex-col">
                                    <div class="flex " :class="{ invalid: !isMilitaryService.isValid }">
                                        <label for="isMilitaryService" class="mr-6">Have you ever been in the military
                                            services?* <span class="text-sm font-light">(If yes please complete the
                                                below)</span></label>
                                        <div class="mr-6 flex items-center">
                                            <input type="radio" v-model="isMilitaryService.val" value="Yes"
                                                @blur="clearValidity('isMilitaryService')" id="militaryServiceYes">
                                            <div class="ml-1">Yes</div>
                                        </div>
                                        <div class="flex items-center">
                                            <input type="radio" v-model="isMilitaryService.val" value="No"
                                                @blur="clearValidity('isMilitaryService')" id="militaryServiceNo">
                                            <div class="ml-1">No</div>
                                        </div>
                                    </div>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!isMilitaryService.isValid" class="h-4 text-xs text-red-600">
                                            {{ isMilitaryService.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                            <div class="grid grid-cols-4 gap-6 mt-6">
                                <div class="flex flex-col" :class="{ invalid: !militaryServiceCountry.isValid }">
                                    <label for="militaryServiceCountry">Country*</label>
                                    <select class="mt-2 ht-42" v-model.trim="militaryServiceCountry.val"
                                        @blur="clearValidity('militaryServiceCountry')" id="militaryServiceCountry">
                                        <option value="">Select an option</option>
                                        <option v-for="(country, index) in countries" :key="index"
                                            :value="country.country_name">{{ country.country_name }} </option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!militaryServiceCountry.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ militaryServiceCountry.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !militaryServiceTypeOfService.isValid }">
                                    <label for="militaryServiceTypeOfService">Type of Service*</label>
                                    <input class="mt-2" type="text" v-model.trim="militaryServiceTypeOfService.val"
                                        @blur="clearValidity('militaryServiceTypeOfService')"
                                        id="militaryServiceTypeOfService">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!militaryServiceTypeOfService.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ militaryServiceTypeOfService.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !militaryServiceRank.isValid }">
                                    <label for="militaryServiceRank">Rank*</label>
                                    <input class="mt-2" type="text" v-model.trim="militaryServiceRank.val"
                                        @blur="clearValidity('militaryServiceRank')" id="militaryServiceRank">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!militaryServiceRank.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ militaryServiceRank.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !militaryServiceDuration.isValid }">
                                    <label for="militaryServiceDuration">Duration*</label>
                                    <input class="mt-2" type="text" v-model.trim="militaryServiceDuration.val"
                                        @blur="clearValidity('militaryServiceDuration')" id="militaryServiceDuration">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!militaryServiceDuration.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ militaryServiceDuration.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Sponsorship Details in United
                            Arab Emirates <span class="text-sm font-light">(If applicable)</span></h2>
                        <div class="p-8">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !initialPortOfEntry.isValid }">
                                    <label for="initialPortOfEntry">Initial Port of Entry to UAE*</label>
                                    <input class="mt-2" type="text" v-model.trim="initialPortOfEntry.val"
                                        @blur="clearValidity('initialPortOfEntry')" id="initialPortOfEntry">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!initialPortOfEntry.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ initialPortOfEntry.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !initialDateOfEntry.isValid }">
                                    <label for="initialDateOfEntry">Initial Date of Entry*</label>
                                    <input class="mt-2" type="date" v-model.trim="initialDateOfEntry.val"
                                        @blur="clearValidity('initialDateOfEntry')" id="initialDateOfEntry">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!initialDateOfEntry.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ initialDateOfEntry.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !initialEmployer.isValid }">
                                    <label for="initialEmployer">Initial Employer*</label>
                                    <input class="mt-2" type="text" v-model.trim="initialEmployer.val"
                                        @blur="clearValidity('initialEmployer')" id="initialEmployer">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!initialEmployer.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ initialEmployer.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !initialJobTitle.isValid }">
                                    <label for="initialJobTitle">Initial Job Title*</label>
                                    <input class="mt-2" type="text" v-model.trim="initialJobTitle.val"
                                        @blur="clearValidity('initialJobTitle')" id="initialJobTitle">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!initialJobTitle.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ initialJobTitle.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !currentSponsor.isValid }">
                                    <label for="currentSponsor">Current Sponsor*</label>
                                    <input class="mt-2" type="text" v-model.trim="currentSponsor.val"
                                        @blur="clearValidity('currentSponsor')" id="currentSponsor">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!currentSponsor.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ currentSponsor.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !currentResidencyNumber.isValid }">
                                    <label for="currentResidencyNumber">Current Residency No*</label>
                                    <input class="mt-2" type="text" v-model.trim="currentResidencyNumber.val"
                                        @blur="clearValidity('currentResidencyNumber')" id="currentResidencyNumber">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!currentResidencyNumber.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ currentResidencyNumber.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !uidNumber.isValid }">
                                    <label for="uidNumber">UID No*</label>
                                    <input class="mt-2" type="text" v-model.trim="uidNumber.val"
                                        @blur="clearValidity('uidNumber')" id="uidNumber">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!uidNumber.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ uidNumber.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col"
                                    :class="{ invalid: !sponsorshipDetailsPlaceOfIssue.isValid }">
                                    <label for="sponsorshipDetailsPlaceOfIssue">Place of Issue*</label>
                                    <input class="mt-2" type="text" v-model.trim="sponsorshipDetailsPlaceOfIssue.val"
                                        @blur="clearValidity('sponsorshipDetailsPlaceOfIssue')"
                                        id="sponsorshipDetailsPlaceOfIssue">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!sponsorshipDetailsPlaceOfIssue.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ sponsorshipDetailsPlaceOfIssue.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !sponsorshipDetailsDateOfIssue.isValid }">
                                    <label for="sponsorshipDetailsDateOfIssue">Date of Issue*</label>
                                    <input class="mt-2" type="date" v-model.trim="sponsorshipDetailsDateOfIssue.val"
                                        @blur="clearValidity('sponsorshipDetailsDateOfIssue')"
                                        id="sponsorshipDetailsDateOfIssue">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!sponsorshipDetailsDateOfIssue.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ sponsorshipDetailsDateOfIssue.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col"
                                    :class="{ invalid: !sponsorshipDetailsDateOfExpiry.isValid }">
                                    <label for="sponsorshipDetailsDateOfExpiry">Date of Expiry*</label>
                                    <input class="mt-2" type="date" v-model.trim="sponsorshipDetailsDateOfExpiry.val"
                                        @blur="clearValidity('sponsorshipDetailsDateOfExpiry')"
                                        id="sponsorshipDetailsDateOfExpiry">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!sponsorshipDetailsDateOfExpiry.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ sponsorshipDetailsDateOfExpiry.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Current Employment
                            Information</h2>
                        <div class="p-8">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !employmentStatus.isValid }">
                                    <label for="employmentStatus">Employment Status*</label>
                                    <select class="mt-2 ht-42" v-model.trim="employmentStatus.val"
                                        @blur="clearValidity('employmentStatus')" id="employmentStatus">
                                        <option value="">Select an option</option>
                                        <option value="Unemployed">Unemployed</option>
                                        <option value="Full Time">Full Time</option>
                                        <option value="Part Time">Part Time</option>
                                        <option value="Consultant">Consultant</option>
                                        <option value="Daily Wages">Daily Wages</option>
                                        <option value="Trainee">Trainee</option>
                                        <option value="Volunteer">Volunteer</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!employmentStatus.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ employmentStatus.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !currentEmployer.isValid }">
                                    <label for="currentEmployer">Current Employer*</label>
                                    <input class="mt-2" type="text" v-model.trim="currentEmployer.val"
                                        @blur="clearValidity('currentEmployer')" id="currentEmployer">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!currentEmployer.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ currentEmployer.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !currentOrLastJobTitle.isValid }">
                                    <label for="currentOrLastJobTitle">Current / Last Job Title*</label>
                                    <input class="mt-2" type="text" v-model.trim="currentOrLastJobTitle.val"
                                        @blur="clearValidity('currentOrLastJobTitle')" id="currentOrLastJobTitle">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!currentOrLastJobTitle.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ currentOrLastJobTitle.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !employerSector.isValid }">
                                    <label for="employerSector">Employer Sector*</label>
                                    <select class="mt-2 ht-42" v-model.trim="employerSector.val"
                                        @blur="clearValidity('employerSector')" id="employerSector">
                                        <option value="">Select an option</option>
                                        <option value="Private">Private</option>
                                        <option value="Semi-Government">Semi-Government</option>
                                        <option value="Government">Government</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!employerSector.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ employerSector.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col"
                                    :class="{ invalid: !currentEmploymentCityOrCountry.isValid }">
                                    <label for="currentEmploymentCityOrCountry">City / Country*</label>
                                    <input class="mt-2" type="text" v-model.trim="currentEmploymentCityOrCountry.val"
                                        @blur="clearValidity('currentEmploymentCityOrCountry')"
                                        id="currentEmploymentCityOrCountry">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!currentEmploymentCityOrCountry.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ currentEmploymentCityOrCountry.msg }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Residence Information in
                            United Arab Emirates <span class="text-sm font-light">(If applicable)</span></h2>
                        <div class="p-8">
                            <div class="grid grid-cols-4 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeProprietor.isValid }">
                                    <label for="resInfoUaeProprietor">Proprietor*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaeProprietor.val"
                                        @blur="clearValidity('resInfoUaeProprietor')" id="resInfoUaeProprietor">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeProprietor.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeProprietor.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeCity.isValid }">
                                    <label for="resInfoUaeCity">City*</label>
                                    <select class="mt-2 ht-42" v-model.trim="resInfoUaeCity.val" id="resInfoUaeCity">
                                        <option value="">Select an option</option>
                                        <option value="Abu Dhabi">Abu Dhabi</option>
                                        <option value="Dubai">Dubai</option>
                                        <option value="Sharjah">Sharjah</option>
                                        <option value="Ajman">Ajman</option>
                                        <option value="Umm Al Quwain">Umm Al Quwain</option>
                                        <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                                        <option value="Fujairah">Fujairah</option>
                                    </select>
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeCity.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeCity.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeZone.isValid }">
                                    <label for="resInfoUaeZone">Zone*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaeZone.val"
                                        @blur="clearValidity('resInfoUaeZone')" id="resInfoUaeZone">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeZone.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeZone.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeStreet.isValid }">
                                    <label for="resInfoUaeStreet">Street*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaeStreet.val"
                                        @blur="clearValidity('resInfoUaeStreet')" id="resInfoUaeStreet">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeStreet.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeStreet.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeHouse.isValid }">
                                    <label for="resInfoUaeHouse">House / Building No*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaeHouse.val"
                                        @blur="clearValidity('resInfoUaeHouse')" id="resInfoUaeHouse">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeHouse.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeHouse.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeFloorNo.isValid }">
                                    <label for="resInfoUaeFloorNo">Floor No*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaeFloorNo.val"
                                        @blur="clearValidity('resInfoUaeFloorNo')" id="resInfoUaeFloorNo">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeFloorNo.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeFloorNo.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaeTel.isValid }">
                                    <label for="resInfoUaeTel">Tel / Mobile</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaeTel.val"
                                        @blur="clearValidity('resInfoUaeTel')" id="resInfoUaeTel">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaeTel.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaeTel.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoUaePoBox.isValid }">
                                    <label for="resInfoUaePoBox">P.O.Box</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoUaePoBox.val"
                                        @blur="clearValidity('resInfoUaePoBox')" id="resInfoUaePoBox">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoUaePoBox.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoUaePoBox.msg }}
                                        </p>
                                    </transition>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Residence Information in Home
                            Country <span class="text-sm font-light">(If applicable)</span></h2>
                        <div class="p-8">
                            <div class="grid grid-cols-4 gap-6">
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryProprietor.isValid }">
                                    <label for="resInfoHomeCountryProprietor">Proprietor*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryProprietor.val"
                                        @blur="clearValidity('resInfoHomeCountryProprietor')"
                                        id="resInfoHomeCountryProprietor">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryProprietor.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryProprietor.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryCity.isValid }">
                                    <label for="resInfoHomeCountryCity">City*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryCity.val"
                                        @blur="clearValidity('resInfoHomeCountryCity')" id="resInfoHomeCountryCity">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryCity.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryCity.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryZone.isValid }">
                                    <label for="resInfoHomeCountryZone">Zone*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryZone.val"
                                        @blur="clearValidity('resInfoHomeCountryZone')" id="resInfoHomeCountryZone">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryZone.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryZone.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryStreet.isValid }">
                                    <label for="resInfoHomeCountryStreet">Street*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryStreet.val"
                                        @blur="clearValidity('resInfoHomeCountryStreet')" id="resInfoHomeCountryStreet">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryStreet.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryStreet.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryHouse.isValid }">
                                    <label for="resInfoHomeCountryHouse">House / Building No*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryHouse.val"
                                        @blur="clearValidity('resInfoHomeCountryHouse')" id="resInfoHomeCountryHouse">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryHouse.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryHouse.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryFloorNo.isValid }">
                                    <label for="resInfoHomeCountryFloorNo">Floor No*</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryFloorNo.val"
                                        @blur="clearValidity('resInfoHomeCountryFloorNo')"
                                        id="resInfoHomeCountryFloorNo">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryFloorNo.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryFloorNo.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryTel.isValid }">
                                    <label for="resInfoHomeCountryTel">Tel / Mobile</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryTel.val"
                                        @blur="clearValidity('resInfoHomeCountryTel')" id="resInfoHomeCountryTel">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryTel.isValid" class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryTel.msg }}
                                        </p>
                                    </transition>
                                </div>
                                <div class="flex flex-col" :class="{ invalid: !resInfoHomeCountryPoBox.isValid }">
                                    <label for="resInfoHomeCountryPoBox">P.O.Box</label>
                                    <input class="mt-2" type="text" v-model.trim="resInfoHomeCountryPoBox.val"
                                        @blur="clearValidity('resInfoHomeCountryPoBox')" id="resInfoHomeCountryPoBox">
                                    <transition name="form-fade" mode="out-in">
                                        <p v-if="!resInfoHomeCountryPoBox.isValid"
                                            class="h-4 text-xs text-red-600 ml-1">
                                            {{ resInfoHomeCountryPoBox.msg }}
                                        </p>
                                    </transition>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col mt-8 border-2 border-ternary rounded-2xl overflow-hidden shadow-sm">
                        <h2 class="text-2xl font-semibold bg-ternary text-white px-6 py-3">Upload Documents</h2>
                        <div class="p-8">
                            <div class="grid grid-cols-1 gap-6">
                                <div class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !signature.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="signature">Signature* <span class="text-sm text-secondary">(PNG max
                                                2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="signature" class="hidden" id="signature" @change="handleSignature"
                                            type="file" accept="image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('signature')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                title="View Signature" v-if="signature.val"
                                                @click.stop="showSignaturePreview = true">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ signature.val.name }}</div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!signature.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ signature.msg }}
                                            </p>
                                        </transition>
                                        <transition name="fade" mode="out-in">
                                            <base-full-modal v-if="showSignaturePreview"
                                                :title="`View Signature - ${signature.val.name}`"
                                                @close="showSignaturePreview = false">
                                                <iframe v-if="signature.val" :src="signaturePreview" width="100%"
                                                    :height="getIframeHeight" frameborder="0"></iframe>
                                            </base-full-modal>
                                        </transition>
                                    </div>
                                </div>
                                <div class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !passport.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="passport">Passport* <span class="text-sm text-secondary">(JPG, JPEG,
                                                PNG, PDF max 2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="passport" class="hidden" id="passport" @change="handlePassport"
                                            type="file" accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('passport')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                title="View Passport" v-if="passport.val"
                                                @click.stop="showPassportPreview = true">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ passport.val.name }}</div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!passport.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ passport.msg }}
                                            </p>
                                        </transition>
                                        <transition name="fade" mode="out-in">
                                            <base-full-modal v-if="showPassportPreview"
                                                :title="`View Passport - ${passport.val.name}`"
                                                @close="showPassportPreview = false">
                                                <iframe v-if="passport.val" :src="passportPreview" width="100%"
                                                    :height="getIframeHeight" frameborder="0"></iframe>
                                            </base-full-modal>
                                        </transition>
                                    </div>
                                </div>
                                <div v-if="isVisaRequired" class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !visa.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="visa">Visa* <span class="text-sm text-secondary">(JPG, JPEG, PNG,
                                                PDF max 2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="visa" class="hidden" id="visa" @change="handleVisa" type="file"
                                            accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('visa')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group" title="View Visa"
                                                v-if="visa.val" @click.stop="showVisaPreview = true">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ visa.val.name }}</div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!visa.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ visa.msg }}
                                            </p>
                                        </transition>
                                        <transition name="fade" mode="out-in">
                                            <base-full-modal v-if="showVisaPreview"
                                                :title="`View Visa - ${visa.val.name}`"
                                                @close="showVisaPreview = false">
                                                <iframe v-if="visa.val" :src="visaPreview" width="100%"
                                                    :height="getIframeHeight" frameborder="0"></iframe>
                                            </base-full-modal>
                                        </transition>
                                    </div>
                                </div>
                                <div class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !emiratesId.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="emiratesId">Emirates ID* <span class="text-sm text-secondary">(JPG,
                                                JPEG, PNG, PDF max 2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="emiratesId" class="hidden" id="emiratesId"
                                            @change="handleEmiratesId" type="file" accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('emiratesId')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                title="View Emirates ID" v-if="emiratesId.val"
                                                @click.stop="showEmiratesIdPreview = true">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ emiratesId.val.name }}
                                                </div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!emiratesId.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ emiratesId.msg }}
                                            </p>
                                        </transition>
                                        <transition name="fade" mode="out-in">
                                            <base-full-modal v-if="showEmiratesIdPreview"
                                                :title="`View Emirates ID - ${emiratesId.val.name}`"
                                                @close="showEmiratesIdPreview = false">
                                                <iframe v-if="emiratesId.val" :src="emiratesIdPreview" width="100%"
                                                    :height="getIframeHeight" frameborder="0"></iframe>
                                            </base-full-modal>
                                        </transition>
                                    </div>
                                </div>

                                <div v-if="showTradeLicense" class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !tradeLicense.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="tradeLicense">Trade License* <span
                                                class="text-sm text-secondary">(JPG, JPEG, PNG, PDF max
                                                2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="tradeLicense" class="hidden" id="tradeLicense"
                                            @change="handleTradeLicense" type="file" accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('tradeLicense')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                title="View Trade License" v-if="tradeLicense.val"
                                                @click.stop="showTradeLicensePreview = true">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ tradeLicense.val.name }}
                                                </div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!tradeLicense.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ tradeLicense.msg }}
                                            </p>
                                        </transition>
                                        <transition name="fade" mode="out-in">
                                            <base-full-modal v-if="showTradeLicensePreview"
                                                :title="`View Trade License - ${tradeLicense.val.name}`"
                                                @close="showTradeLicensePreview = false">
                                                <iframe v-if="tradeLicense.val" :src="tradeLicensePreview" width="100%"
                                                    :height="getIframeHeight" frameborder="0"></iframe>
                                            </base-full-modal>
                                        </transition>
                                    </div>
                                </div>

                            </div>

                            <div v-if="applicationType.val == 'Individual'" class="grid grid-cols-1 gap-6 mt-6">
                                <div class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !academicQualificationCertificate.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="academicQualificationCertificate">Academic Qualification* <span
                                                class="text-sm text-secondary">(JPG, JPEG, PNG, PDF max
                                                2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="academicQualificationCertificate" class="hidden"
                                            id="academicQualificationCertificate"
                                            @change="handleAcademicQualificationCertificate" type="file"
                                            accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('academicQualificationCertificate')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                v-if="academicQualificationCertificate.val">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{
                                                    academicQualificationCertificate.val.name }}</div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!academicQualificationCertificate.isValid"
                                                class="h-4 text-xs text-red-600 ml-1">
                                                {{ academicQualificationCertificate.msg }}
                                            </p>
                                        </transition>
                                    </div>
                                </div>
                                <div class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !employmentCard.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="employmentCard">Employment Card* <span
                                                class="text-sm text-secondary">(JPG, JPEG, PNG, PDF max
                                                2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="employmentCard" class="hidden" id="employmentCard"
                                            @change="handleEmploymentCard" type="file" accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('employmentCard')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                v-if="employmentCard.val">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ employmentCard.val.name }}
                                                </div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!employmentCard.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ employmentCard.msg }}
                                            </p>
                                        </transition>
                                    </div>
                                </div>
                                <div class="flex flex-row flex-wrap items-center" :class="{ invalid: !cv.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="cv">CV* <span class="text-sm text-secondary">(JPG, JPEG, PNG, PDF
                                                max 2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="cv" class="hidden" id="cv" @change="handleCv" type="file"
                                            accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('cv')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group" v-if="cv.val">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ cv.val.name }}</div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!cv.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ cv.msg }}
                                            </p>
                                        </transition>
                                    </div>
                                </div>
                                <div class="flex flex-row flex-wrap items-center"
                                    :class="{ invalid: !contentPresented.isValid }">
                                    <div class="w-full md:w-1/2 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
                                        <label for="contentPresented">Copy of the content presented* <span
                                                class="text-sm text-secondary">(JPG, JPEG, PNG, PDF max
                                                2MB)</span></label>
                                    </div>
                                    <div class="w-full md:w-1/2 lg:w-3/5 xl:w-2/3 2xl:w-3/4 mt-2 md:mt-0">
                                        <input ref="contentPresented" class="hidden" id="contentPresented"
                                            @change="handleContentPresented" type="file"
                                            accept="application/pdf,image/*">
                                        <div class="flex items-center">
                                            <div @click.stop="onPickFile('contentPresented')"
                                                class="rounded-lg h-10 border border-ternary bg-white hover:bg-ternary text-ternary hover:text-white px-6 text-sm uppercase cursor-pointer flex items-center justify-center">
                                                Upload</div>
                                            <div class="ml-2 flex items-center cursor-pointer group"
                                                v-if="contentPresented.val">
                                                <icon-document class="h-4 w-4 flex-shrink-0"></icon-document>
                                                <div class="ml-0.5 leading-tight text-xs">{{ contentPresented.val.name
                                                    }}</div>
                                            </div>
                                        </div>
                                        <transition name="form-fade" mode="out-in">
                                            <p v-if="!contentPresented.isValid" class="h-4 text-xs text-red-600 ml-1">
                                                {{ contentPresented.msg }}
                                            </p>
                                        </transition>
                                    </div>
                                </div>
                            </div>


                        </div>
                    </div>
                    <div class="mt-8 flex flex-col items-center">
                        <div class="mb-4 text-sm text-gray-600">
                            Total file size: {{ formatFileSize(calculateTotalFileSize()) }} / 50MB
                        </div>
                        <div @click.stop="submitForm"
                            class="flex justify-center items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">
                            Submit</div>
                    </div>

                </div>
            </div>
        </div>
        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconDocument from "../../components/icons/IconDocument.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconClose from "../../components/icons/IconClose.vue";
import { formattedDateWithTimeAmPm } from "../../helpers/utils";

const GET_SECURITY_CLEARANCE_INITIATION_PUBLIC = gql`
    query GetSecurityClearanceInitiationPublic($token: String!) {
        getSecurityClearanceInitiationPublic(token: $token) {
            error
            error_code
            message
            initiation {
                id
                token
                speaker
                event
                purpose_of_event
                target_group
                age_group
                date_time
                end_date
                entity_location
                requester
                supervisor
                send_to_email
                created_at
            }
        }
    }
`;
export default {
    name: "security-clearance-form",
    components: {
        IconDocument,
        IconClose,
        IconPlus,
    },
    data() {
        return {
            eventDetails: null,
            eventLoading: false,
            eventError: null,
            entity: {
                val: "",
                isValid: true,
                msg: "",
            },
            applicationType: {
                val: "",
                isValid: true,
                msg: "",
            },
            purposeOfRequest: {
                val: "",
                isValid: true,
                msg: "",
            },
            otherPurposeOfRequest: {
                val: "",
                isValid: true,
                msg: "",
            },
            displayOtherPurposeOfRequest: false,
            profilePicturePreview: null,
            profilePicture: {
                val: null,
                isValid: true,
                msg: "",
            },
            fullName: {
                val: "",
                isValid: true,
                msg: "",
            },
            dateOfBirth: {
                val: null,
                isValid: true,
                msg: "",
            },
            placeOfBirth: {
                val: "",
                isValid: true,
                msg: "",
            },
            currentNationality: {
                val: "",
                isValid: true,
                msg: "",
            },
            previousNationality: {
                val: "",
                isValid: true,
                msg: "",
            },
            passportNumber: {
                val: "",
                isValid: true,
                msg: "",
            },
            placeOfIssue: {
                val: "",
                isValid: true,
                msg: "",
            },
            dateOfIssue: {
                val: null,
                isValid: true,
                msg: "",
            },
            dateOfExpiry: {
                val: null,
                isValid: true,
                msg: "",
            },
            maritalStatus: {
                val: "",
                isValid: true,
                msg: "",
            },
            religion: {
                val: "",
                isValid: true,
                msg: "",
            },
            otherReligion: {
                val: "",
                isValid: true,
                msg: "",
            },
            displayOtherReligion: false,
            sect: {
                val: "",
                isValid: true,
                msg: "",
            },
            motherTongue: {
                val: "",
                isValid: true,
                msg: "",
            },
            otherLanguagesKnown: {
                val: "",
                isValid: true,
                msg: "",
            },
            fatherName: {
                val: "",
                isValid: true,
                msg: "",
            },
            fatherNationality: {
                val: "",
                isValid: true,
                msg: "",
            },
            fatherProfession: {
                val: "",
                isValid: true,
                msg: "",
            },
            fatherEmployer: {
                val: "",
                isValid: true,
                msg: "",
            },
            fatherDateOfBirth: {
                val: null,
                isValid: true,
                msg: "",
            },
            fatherPlaceOfBirth: {
                val: null,
                isValid: true,
                msg: "",
            },
            motherName: {
                val: "",
                isValid: true,
                msg: "",
            },
            motherNationality: {
                val: "",
                isValid: true,
                msg: "",
            },
            motherProfession: {
                val: "",
                isValid: true,
                msg: "",
            },
            motherEmployer: {
                val: "",
                isValid: true,
                msg: "",
            },
            motherDateOfBirth: {
                val: null,
                isValid: true,
                msg: "",
            },
            motherPlaceOfBirth: {
                val: "",
                isValid: true,
                msg: "",
            },
            highestQualification: {
                val: "",
                isValid: true,
                msg: "",
            },
            major: {
                val: "",
                isValid: true,
                msg: "",
            },
            institution: {
                val: "",
                isValid: true,
                msg: "",
            },
            academicQualificationCountry: {
                val: "",
                isValid: true,
                msg: "",
            },
            academicQualificationYear: {
                val: "",
                isValid: true,
                msg: "",
            },
            isMilitaryService: {
                val: null,
                isValid: true,
                msg: "",
            },
            militaryServiceCountry: {
                val: "",
                isValid: true,
                msg: "",
            },
            militaryServiceTypeOfService: {
                val: "",
                isValid: true,
                msg: "",
            },
            militaryServiceRank: {
                val: "",
                isValid: true,
                msg: "",
            },
            militaryServiceDuration: {
                val: "",
                isValid: true,
                msg: "",
            },
            initialPortOfEntry: {
                val: "",
                isValid: true,
                msg: "",
            },
            initialDateOfEntry: {
                val: null,
                isValid: true,
                msg: "",
            },
            initialEmployer: {
                val: "",
                isValid: true,
                msg: "",
            },
            initialJobTitle: {
                val: "",
                isValid: true,
                msg: "",
            },
            currentSponsor: {
                val: "",
                isValid: true,
                msg: "",
            },
            currentResidencyNumber: {
                val: "",
                isValid: true,
                msg: "",
            },
            uidNumber: {
                val: "",
                isValid: true,
                msg: "",
            },
            sponsorshipDetailsPlaceOfIssue: {
                val: "",
                isValid: true,
                msg: "",
            },
            sponsorshipDetailsDateOfIssue: {
                val: null,
                isValid: true,
                msg: "",
            },
            sponsorshipDetailsDateOfExpiry: {
                val: null,
                isValid: true,
                msg: "",
            },
            employmentStatus: {
                val: "",
                isValid: true,
                msg: "",
            },
            currentEmployer: {
                val: "",
                isValid: true,
                msg: "",
            },
            currentOrLastJobTitle: {
                val: "",
                isValid: true,
                msg: "",
            },
            employerSector: {
                val: "",
                isValid: true,
                msg: "",
            },
            currentEmploymentCityOrCountry: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeProprietor: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeCity: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeZone: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeStreet: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeHouse: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeFloorNo: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaeTel: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoUaePoBox: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryProprietor: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryCity: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryZone: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryStreet: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryHouse: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryFloorNo: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryTel: {
                val: "",
                isValid: true,
                msg: "",
            },
            resInfoHomeCountryPoBox: {
                val: "",
                isValid: true,
                msg: "",
            },
            signaturePreview: null,
            signature: {
                val: null,
                isValid: true,
                msg: "",
            },
            showSignaturePreview: false,
            passportPreview: null,
            passport: {
                val: null,
                isValid: true,
                msg: "",
            },
            showPassportPreview: false,
            visaPreview: null,
            visa: {
                val: null,
                isValid: true,
                msg: "",
            },
            showVisaPreview: false,
            emiratesIdPreview: null,
            emiratesId: {
                val: null,
                isValid: true,
                msg: "",
            },
            showEmiratesIdPreview: false,
            tradeLicensePreview: null,
            tradeLicense: {
                val: null,
                isValid: true,
                msg: "",
            },
            showTradeLicensePreview: false,
            academicQualificationCertificate: {
                val: null,
                isValid: true,
                msg: "",
            },
            employmentCard: {
                val: null,
                isValid: true,
                msg: "",
            },
            cv: {
                val: null,
                isValid: true,
                msg: "",
            },
            contentPresented: {
                val: null,
                isValid: true,
                msg: "",
            },
            formIsValid: true,
            isLoading: false,
        };
    },
    created() {
        this.fetchInitiationDetails();
    },
    watch: {
        'applicationType.val': function () {
            this.purposeOfRequest = {
                val: "",
                isValid: true,
                msg: "",
            };
            this.academicQualificationCertificate = {
                val: null,
                isValid: true,
                msg: "",
            };
            this.employmentCard = {
                val: null,
                isValid: true,
                msg: "",
            };
            this.cv = {
                val: null,
                isValid: true,
                msg: "",
            };
            this.contentPresented = {
                val: null,
                isValid: true,
                msg: "",
            };
        },
        'purposeOfRequest.val': function (newVal) {
            if (newVal == 'Other') {
                this.displayOtherPurposeOfRequest = true;
            } else {
                this.otherPurposeOfRequest = {
                    val: "",
                    isValid: true,
                    msg: "",
                };
                this.displayOtherPurposeOfRequest = false;
            }
        },
        'religion.val': function (newVal) {
            if (newVal == 'Other') {
                this.displayOtherReligion = true;
            } else {
                this.otherReligion = {
                    val: "",
                    isValid: true,
                    msg: "",
                };
                this.displayOtherReligion = false;
            }
        },
        'isMilitaryService.val': function () {
            this.militaryServiceCountry = {
                val: "",
                isValid: true,
                msg: "",
            };
            this.militaryServiceTypeOfService = {
                val: "",
                isValid: true,
                msg: "",
            };
            this.militaryServiceRank = {
                val: "",
                isValid: true,
                msg: "",
            };
            this.militaryServiceDuration = {
                val: "",
                isValid: true,
                msg: "",
            };
        },
    },
    computed: {
        isVisaRequired() {
            if (this.currentNationality.val == 'United Arab Emirates') {
                return false;
            } else {
                return true;
            }
        },
        getIframeHeight() {
            return `${window.innerHeight} px`;
        },
        getPurposeOfRequest() {
            if (this.applicationType.val == 'Individual') {
                return [
                    "Speaker / Lecturer",
                    "Meeting",
                    "Any Student Engagement",
                    "Other",
                ];
            } else if (this.applicationType.val == 'Company') {
                return [
                    "Signing Agreement / MOU",
                    "Other",
                ];
            } else {
                return [];
            }
        },
        showTradeLicense() {
            return this.applicationType.val == 'Company' && this.purposeOfRequest.val == 'Signing Agreement / MOU';
        },
    },
    apollo: {
        countries() {
            return {
                query: gql`query GetEntityList {
                    countries {
                        country_name
                    }
                }`,
            }
        },
    },
    methods: {
        async fetchInitiationDetails() {
            const token = this.$route.params.token;
            if (!token) {
                this.eventDetails = null;
                this.eventError = 'Invalid security clearance link.';
                return;
            }
            this.eventLoading = true;
            this.eventError = null;
            try {
                const response = await this.$apollo.query({
                    query: GET_SECURITY_CLEARANCE_INITIATION_PUBLIC,
                    variables: { token },
                    fetchPolicy: 'network-only'
                });
                const payload = response && response.data ? response.data.getSecurityClearanceInitiationPublic : null;
                if (!payload || payload.error || !payload.initiation) {
                    this.eventDetails = null;
                    this.eventError = payload ? payload.message : 'Unable to load event details.';
                } else {
                    this.eventDetails = payload.initiation;
                }
            } catch (error) {
                this.eventDetails = null;
                this.eventError = error.message || 'Unable to load event details.';
            } finally {
                this.eventLoading = false;
            }
        },
        formatEventDateTime(value) {
            if (!value) {
                return '-';
            }
            try {
                return formattedDateWithTimeAmPm(value);
            } catch (_error) {
                return value;
            }
        },
        displayValue(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }
            return value;
        },
        /** HTML date inputs use "" when cleared; GraphQL Date rejects "" and accepts null. */
        dateFieldIsEmpty(val) {
            return val === null || val === undefined || val === '';
        },
        normalizeGraphQlDate(val) {
            if (this.dateFieldIsEmpty(val)) {
                return null;
            }
            return val;
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        cancelUploadedPhoto() {
            this.profilePicture.val = null;
            this.profilePicturePreview = null;
        },
        handleProfilePicture(event) {
            this.profilePicture.isValid = true;
            this.profilePicture.msg = "";
            const file = event.target.files[0];
            this.profilePicture.val = file;
            if (file) {
                this.profilePicturePreview = URL.createObjectURL(file);
            }
        },
        onPickFile(ref) {
            this.$refs[ref].click();
        },
        async validateForm() {
            this.formIsValid = true;

            if (this.applicationType.val == "") {
                this.applicationType.isValid = false;
                this.applicationType.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.purposeOfRequest.val == "") {
                this.purposeOfRequest.isValid = false;
                this.purposeOfRequest.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.displayOtherPurposeOfRequest) {
                if (this.otherPurposeOfRequest.val == "") {
                    this.otherPurposeOfRequest.isValid = false;
                    this.otherPurposeOfRequest.msg = "You must enter the purpose of request.";
                    this.formIsValid = false;
                }
            }
            if (this.entity.val == "") {
                this.entity.isValid = false;
                this.entity.msg = "You must select an entity.";
                this.formIsValid = false;
            }
            if (this.profilePicture.val === null) {
                this.profilePicture.isValid = false;
                this.profilePicture.msg = "You must upload a photo.";
                this.formIsValid = false;
            }
            if (this.fullName.val === "") {
                this.fullName.isValid = false;
                this.fullName.msg = "Full Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.dateFieldIsEmpty(this.dateOfBirth.val)) {
                this.dateOfBirth.isValid = false;
                this.dateOfBirth.msg = "Date of Birth must not be empty.";
                this.formIsValid = false;
            }
            if (this.placeOfBirth.val === "") {
                this.placeOfBirth.isValid = false;
                this.placeOfBirth.msg = "Place of Birth must not be empty.";
                this.formIsValid = false;
            }
            if (this.currentNationality.val === "") {
                this.currentNationality.isValid = false;
                this.currentNationality.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.previousNationality.val === "") {
                this.previousNationality.isValid = false;
                this.previousNationality.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.passportNumber.val === "") {
                this.passportNumber.isValid = false;
                this.passportNumber.msg = "Passport Number must not be empty.";
                this.formIsValid = false;
            }
            if (this.placeOfIssue.val === "") {
                this.placeOfIssue.isValid = false;
                this.placeOfIssue.msg = "Place of Issue must not be empty.";
                this.formIsValid = false;
            }
            if (this.dateFieldIsEmpty(this.dateOfIssue.val)) {
                this.dateOfIssue.isValid = false;
                this.dateOfIssue.msg = "Date of Issue must not be empty.";
                this.formIsValid = false;
            }
            if (this.dateFieldIsEmpty(this.dateOfExpiry.val)) {
                this.dateOfExpiry.isValid = false;
                this.dateOfExpiry.msg = "Date of Expiry must not be empty.";
                this.formIsValid = false;
            }
            if (this.maritalStatus.val === "") {
                this.maritalStatus.isValid = false;
                this.maritalStatus.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.religion.val === "") {
                this.religion.isValid = false;
                this.religion.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.displayOtherReligion) {
                if (this.otherReligion.val === "") {
                    this.otherReligion.isValid = false;
                    this.otherReligion.msg = "You must enter your religion.";
                    this.formIsValid = false;
                }
            }
            if (this.sect.val === "") {
                this.sect.isValid = false;
                this.sect.msg = "You must select an option.";
                this.formIsValid = false;
            }
            if (this.motherTongue.val === "") {
                this.motherTongue.isValid = false;
                this.motherTongue.msg = "MotherTongue must not be empty.";
                this.formIsValid = false;
            }
            if (this.fatherName.val === "") {
                this.fatherName.isValid = false;
                this.fatherName.msg = "Father's Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.fatherNationality.val === "") {
                this.fatherNationality.isValid = false;
                this.fatherNationality.msg = "Nationality must not be empty.";
                this.formIsValid = false;
            }
            if (this.fatherProfession.val === "") {
                this.fatherProfession.isValid = false;
                this.fatherProfession.msg = "Profession must not be empty.";
                this.formIsValid = false;
            }
            // if (this.fatherEmployer.val === "") {
            //     this.fatherEmployer.isValid = false;
            //     this.fatherEmployer.msg = "Employer must not be empty.";
            //     this.formIsValid = false;
            // }
            if (this.dateFieldIsEmpty(this.fatherDateOfBirth.val)) {
                this.fatherDateOfBirth.isValid = false;
                this.fatherDateOfBirth.msg = "Date of Birth must not be empty.";
                this.formIsValid = false;
            }
            if (this.fatherPlaceOfBirth.val === null) {
                this.fatherPlaceOfBirth.isValid = false;
                this.fatherPlaceOfBirth.msg = "Place of Birth must not be empty.";
                this.formIsValid = false;
            }
            if (this.motherName.val === "") {
                this.motherName.isValid = false;
                this.motherName.msg = "Mother's Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.motherNationality.val === "") {
                this.motherNationality.isValid = false;
                this.motherNationality.msg = "Nationality must not be empty.";
                this.formIsValid = false;
            }
            if (this.motherProfession.val === "") {
                this.motherProfession.isValid = false;
                this.motherProfession.msg = "Profession must not be empty.";
                this.formIsValid = false;
            }
            // if (this.motherEmployer.val === "") {
            //     this.motherEmployer.isValid = false;
            //     this.motherEmployer.msg = "Employer must not be empty.";
            //     this.formIsValid = false;
            // }
            if (this.dateFieldIsEmpty(this.motherDateOfBirth.val)) {
                this.motherDateOfBirth.isValid = false;
                this.motherDateOfBirth.msg = "Date of Birth must not be empty.";
                this.formIsValid = false;
            }
            if (this.motherPlaceOfBirth.val === "") {
                this.motherPlaceOfBirth.isValid = false;
                this.motherPlaceOfBirth.msg = "Place of Birth must not be empty.";
                this.formIsValid = false;
            }
            if (this.highestQualification.val === "") {
                this.highestQualification.isValid = false;
                this.highestQualification.msg = "Highest Qualification must not be empty.";
                this.formIsValid = false;
            }
            if (this.major.val === "") {
                this.major.isValid = false;
                this.major.msg = "Major must not be empty.";
                this.formIsValid = false;
            }
            if (this.institution.val === "") {
                this.institution.isValid = false;
                this.institution.msg = "Institution must not be empty.";
                this.formIsValid = false;
            }
            if (this.academicQualificationCountry.val === "") {
                this.academicQualificationCountry.isValid = false;
                this.academicQualificationCountry.msg = "You must select a country.";
                this.formIsValid = false;
            }
            if (this.academicQualificationYear.val === "") {
                this.academicQualificationYear.isValid = false;
                this.academicQualificationYear.msg = "Year must not be empty.";
                this.formIsValid = false;
            }
            if (this.isMilitaryService.val === null) {
                this.isMilitaryService.isValid = false;
                this.isMilitaryService.msg = "Choose any of the option.";
                this.formIsValid = false;
            }
            if (this.isMilitaryService.val == "Yes") {
                if (this.militaryServiceCountry.val === "") {
                    this.militaryServiceCountry.isValid = false;
                    this.militaryServiceCountry.msg = "You must select a country.";
                    this.formIsValid = false;
                }
                if (this.militaryServiceTypeOfService.val === "") {
                    this.militaryServiceTypeOfService.isValid = false;
                    this.militaryServiceTypeOfService.msg = "Type of Service must not be empty.";
                    this.formIsValid = false;
                }
                if (this.militaryServiceRank.val === "") {
                    this.militaryServiceRank.isValid = false;
                    this.militaryServiceRank.msg = "Rank must not be empty.";
                    this.formIsValid = false;
                }
                if (this.militaryServiceDuration.val === "") {
                    this.militaryServiceDuration.isValid = false;
                    this.militaryServiceDuration.msg = "Duration must not be empty.";
                    this.formIsValid = false;
                }
            }

            if (this.initialPortOfEntry.val != "" || !this.dateFieldIsEmpty(this.initialDateOfEntry.val) || this.initialEmployer.val != "" || this.initialJobTitle.val != "" || this.currentSponsor.val != "" || this.currentResidencyNumber.val != "" || this.uidNumber.val != "" || this.sponsorshipDetailsPlaceOfIssue.val != "" || !this.dateFieldIsEmpty(this.sponsorshipDetailsDateOfIssue.val) || !this.dateFieldIsEmpty(this.sponsorshipDetailsDateOfExpiry.val)) {
                if (this.initialPortOfEntry.val === "") {
                    this.initialPortOfEntry.isValid = false;
                    this.initialPortOfEntry.msg = "Initial Port of Entry to UAE must not be empty.";
                    this.formIsValid = false;
                }
                if (this.dateFieldIsEmpty(this.initialDateOfEntry.val)) {
                    this.initialDateOfEntry.isValid = false;
                    this.initialDateOfEntry.msg = "Initial Date of Entry must not be empty.";
                    this.formIsValid = false;
                }
                if (this.initialEmployer.val === "") {
                    this.initialEmployer.isValid = false;
                    this.initialEmployer.msg = "Initial Employer must not be empty.";
                    this.formIsValid = false;
                }
                if (this.initialJobTitle.val === "") {
                    this.initialJobTitle.isValid = false;
                    this.initialJobTitle.msg = "Initial Job Title must not be empty.";
                    this.formIsValid = false;
                }
                if (this.currentSponsor.val === "") {
                    this.currentSponsor.isValid = false;
                    this.currentSponsor.msg = "Current Sponsor must not be empty.";
                    this.formIsValid = false;
                }
                if (this.currentResidencyNumber.val === "") {
                    this.currentResidencyNumber.isValid = false;
                    this.currentResidencyNumber.msg = "Current Residency No must not be empty.";
                    this.formIsValid = false;
                }
                if (this.uidNumber.val === "") {
                    this.uidNumber.isValid = false;
                    this.uidNumber.msg = "UID No must not be empty.";
                    this.formIsValid = false;
                }
                if (this.sponsorshipDetailsPlaceOfIssue.val === "") {
                    this.sponsorshipDetailsPlaceOfIssue.isValid = false;
                    this.sponsorshipDetailsPlaceOfIssue.msg = "Place of Issue must not be empty.";
                    this.formIsValid = false;
                }
                if (this.dateFieldIsEmpty(this.sponsorshipDetailsDateOfIssue.val)) {
                    this.sponsorshipDetailsDateOfIssue.isValid = false;
                    this.sponsorshipDetailsDateOfIssue.msg = "Date of Issue must not be empty.";
                    this.formIsValid = false;
                }
                if (this.dateFieldIsEmpty(this.sponsorshipDetailsDateOfExpiry.val)) {
                    this.sponsorshipDetailsDateOfExpiry.isValid = false;
                    this.sponsorshipDetailsDateOfExpiry.msg = "Date of Expiry must not be empty.";
                    this.formIsValid = false;
                }
            } else {
                this.initialPortOfEntry.isValid = true;
                this.initialPortOfEntry.msg = "";
                this.initialDateOfEntry.isValid = true;
                this.initialDateOfEntry.msg = "";
                this.initialEmployer.isValid = true;
                this.initialEmployer.msg = "";
                this.initialJobTitle.isValid = true;
                this.initialJobTitle.msg = "";
                this.currentSponsor.isValid = true;
                this.currentSponsor.msg = "";
                this.currentResidencyNumber.isValid = true;
                this.currentResidencyNumber.msg = "";
                this.uidNumber.isValid = true;
                this.uidNumber.msg = "";
                this.sponsorshipDetailsPlaceOfIssue.isValid = true;
                this.sponsorshipDetailsPlaceOfIssue.msg = "";
                this.sponsorshipDetailsDateOfIssue.isValid = true;
                this.sponsorshipDetailsDateOfIssue.msg = "";
                this.sponsorshipDetailsDateOfExpiry.isValid = true;
                this.sponsorshipDetailsDateOfExpiry.msg = "";
            }
            if (this.employmentStatus.val === "") {
                this.employmentStatus.isValid = false;
                this.employmentStatus.msg = "Employment Status must not be empty.";
                this.formIsValid = false;
            }
            if (this.currentEmployer.val === "") {
                this.currentEmployer.isValid = false;
                this.currentEmployer.msg = "Current Employer must not be empty.";
                this.formIsValid = false;
            }
            if (this.currentOrLastJobTitle.val === "") {
                this.currentOrLastJobTitle.isValid = false;
                this.currentOrLastJobTitle.msg = "Current / Last Job Title must not be empty.";
                this.formIsValid = false;
            }
            if (this.employerSector.val === "") {
                this.employerSector.isValid = false;
                this.employerSector.msg = "Employer Sector must not be empty.";
                this.formIsValid = false;
            }
            if (this.currentEmploymentCityOrCountry.val === "") {
                this.currentEmploymentCityOrCountry.isValid = false;
                this.currentEmploymentCityOrCountry.msg = "City / Country must not be empty.";
                this.formIsValid = false;
            }
            if (this.resInfoUaeProprietor.val != "" || this.resInfoUaeCity.val != "" || this.resInfoUaeZone.val != "" || this.resInfoUaeStreet.val != "" || this.resInfoUaeHouse.val != "" || this.resInfoUaeFloorNo.val != "" || this.resInfoUaeTel.val != "" || this.resInfoUaePoBox.val != "") {
                if (this.resInfoUaeProprietor.val === "") {
                    this.resInfoUaeProprietor.isValid = false;
                    this.resInfoUaeProprietor.msg = "Proprietor must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaeCity.val === "") {
                    this.resInfoUaeCity.isValid = false;
                    this.resInfoUaeCity.msg = "City must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaeZone.val === "") {
                    this.resInfoUaeZone.isValid = false;
                    this.resInfoUaeZone.msg = "Zone must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaeStreet.val === "") {
                    this.resInfoUaeStreet.isValid = false;
                    this.resInfoUaeStreet.msg = "Street must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaeHouse.val === "") {
                    this.resInfoUaeHouse.isValid = false;
                    this.resInfoUaeHouse.msg = "House / Building No must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaeFloorNo.val === "") {
                    this.resInfoUaeFloorNo.isValid = false;
                    this.resInfoUaeFloorNo.msg = "Floor No must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaeTel.val === "") {
                    this.resInfoUaeTel.isValid = false;
                    this.resInfoUaeTel.msg = "Tel / Mobile must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoUaePoBox.val === "") {
                    this.resInfoUaePoBox.isValid = false;
                    this.resInfoUaePoBox.msg = "P.O.Box must not be empty.";
                    this.formIsValid = false;
                }
            } else {
                this.resInfoUaeProprietor.isValid = true;
                this.resInfoUaeProprietor.msg = "";
                this.resInfoUaeCity.isValid = true;
                this.resInfoUaeCity.msg = "";
                this.resInfoUaeZone.isValid = true;
                this.resInfoUaeZone.msg = "";
                this.resInfoUaeStreet.isValid = true;
                this.resInfoUaeStreet.msg = "";
                this.resInfoUaeHouse.isValid = true;
                this.resInfoUaeHouse.msg = "";
                this.resInfoUaeFloorNo.isValid = true;
                this.resInfoUaeFloorNo.msg = "";
                this.resInfoUaeTel.isValid = true;
                this.resInfoUaeTel.msg = "";
                this.resInfoUaePoBox.isValid = true;
                this.resInfoUaePoBox.msg = "";
            }
            if (this.resInfoHomeCountryProprietor.val != "" || this.resInfoHomeCountryCity.val != "" || this.resInfoHomeCountryZone.val != "" || this.resInfoHomeCountryStreet.val != "" || this.resInfoHomeCountryHouse.val != "" || this.resInfoHomeCountryFloorNo.val != "" || this.resInfoHomeCountryTel.val != "" || this.resInfoHomeCountryPoBox.val != "") {
                if (this.resInfoHomeCountryProprietor.val === "") {
                    this.resInfoHomeCountryProprietor.isValid = false;
                    this.resInfoHomeCountryProprietor.msg = "Proprietor must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryCity.val === "") {
                    this.resInfoHomeCountryCity.isValid = false;
                    this.resInfoHomeCountryCity.msg = "City must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryZone.val === "") {
                    this.resInfoHomeCountryZone.isValid = false;
                    this.resInfoHomeCountryZone.msg = "Zone must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryStreet.val === "") {
                    this.resInfoHomeCountryStreet.isValid = false;
                    this.resInfoHomeCountryStreet.msg = "Street must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryHouse.val === "") {
                    this.resInfoHomeCountryHouse.isValid = false;
                    this.resInfoHomeCountryHouse.msg = "House / Building No must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryFloorNo.val === "") {
                    this.resInfoHomeCountryFloorNo.isValid = false;
                    this.resInfoHomeCountryFloorNo.msg = "Floor No must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryTel.val === "") {
                    this.resInfoHomeCountryTel.isValid = false;
                    this.resInfoHomeCountryTel.msg = "Tel / Mobile must not be empty.";
                    this.formIsValid = false;
                }
                if (this.resInfoHomeCountryPoBox.val === "") {
                    this.resInfoHomeCountryPoBox.isValid = false;
                    this.resInfoHomeCountryPoBox.msg = "P.O.Box must not be empty.";
                    this.formIsValid = false;
                }
            } else {
                this.resInfoHomeCountryProprietor.isValid = true;
                this.resInfoHomeCountryProprietor.msg = "";
                this.resInfoHomeCountryCity.isValid = true;
                this.resInfoHomeCountryCity.msg = "";
                this.resInfoHomeCountryZone.isValid = true;
                this.resInfoHomeCountryZone.msg = "";
                this.resInfoHomeCountryStreet.isValid = true;
                this.resInfoHomeCountryStreet.msg = "";
                this.resInfoHomeCountryHouse.isValid = true;
                this.resInfoHomeCountryHouse.msg = "";
                this.resInfoHomeCountryFloorNo.isValid = true;
                this.resInfoHomeCountryFloorNo.msg = "";
                this.resInfoHomeCountryTel.isValid = true;
                this.resInfoHomeCountryTel.msg = "";
                this.resInfoHomeCountryPoBox.isValid = true;
                this.resInfoHomeCountryPoBox.msg = "";
            }
            if (this.signature.val === null) {
                if (this.signature.isValid) {
                    this.signature.isValid = false;
                    this.signature.msg = "You must upload the Signature.";
                }
                this.formIsValid = false;
            }
            if (this.passport.val === null) {
                if (this.passport.isValid) {
                    this.passport.isValid = false;
                    this.passport.msg = "You must upload your Passport.";
                }
                this.formIsValid = false;
            }

            if (this.isVisaRequired) {
                if (this.visa.val === null) {
                    if (this.visa.isValid) {
                        this.visa.isValid = false;
                        this.visa.msg = "You must upload your Visa.";
                    }
                    this.formIsValid = false;
                }
            }
            if (this.emiratesId.val === null) {
                if (this.emiratesId.isValid) {
                    this.emiratesId.isValid = false;
                    this.emiratesId.msg = "You must upload your Emirates ID.";
                }
                this.formIsValid = false;
            }

            if (this.showTradeLicense) {
                if (this.tradeLicense.val === null) {
                    if (this.tradeLicense.isValid) {
                        this.tradeLicense.isValid = false;
                        this.tradeLicense.msg = "You must upload the Trade License.";
                    }
                    this.formIsValid = false;
                }
            }

            if (this.applicationType.val === 'Individual') {
                if (this.academicQualificationCertificate.val === null) {
                    if (this.academicQualificationCertificate.isValid) {
                        this.academicQualificationCertificate.isValid = false;
                        this.academicQualificationCertificate.msg = "You must upload your copy of academic qualification.";
                    }
                    this.formIsValid = false;
                }
                if (this.employmentCard.val === null) {
                    if (this.employmentCard.isValid) {
                        this.employmentCard.isValid = false;
                        this.employmentCard.msg = "You must upload your copy of employment card.";
                    }
                    this.formIsValid = false;
                }
                if (this.cv.val === null) {
                    if (this.cv.isValid) {
                        this.cv.isValid = false;
                        this.cv.msg = "You must upload your CV.";
                    }
                    this.formIsValid = false;
                }
                if (this.contentPresented.val === null) {
                    if (this.contentPresented.isValid) {
                        this.contentPresented.isValid = false;
                        this.contentPresented.msg = "You must upload the copy of content presented.";
                    }
                    this.formIsValid = false;
                }
            }
        },
        handleSignature(event) {
            this.signature.isValid = true;
            this.signature.msg = "";
            const file = event.target.files[0];

            // Check if a file is selected
            if (file) {
                // Check if the file is a PDF
                if (!file.type.includes('image/')) {
                    this.signature.isValid = false;
                    this.signature.msg = 'Only PNG files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    // Check if the file size is greater than 2MB
                    this.signature.isValid = false;
                    this.signature.msg = 'File size must be less than 2MB.';
                } else {
                    // File is valid
                    this.signature.isValid = true;
                    this.signature.msg = '';
                    this.signature.val = file;
                    this.signaturePreview = URL.createObjectURL(file);
                }
            }
        },
        handlePassport(event) {
            this.passport.isValid = true;
            this.passport.msg = "";
            const file = event.target.files[0];

            // Check if a file is selected
            if (file) {
                // Check if the file is a PDF
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.passport.isValid = false;
                    this.passport.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    // Check if the file size is greater than 2MB
                    this.passport.isValid = false;
                    this.passport.msg = 'File size must be less than 2MB.';
                } else {
                    // File is valid
                    this.passport.isValid = true;
                    this.passport.msg = '';
                    this.passport.val = file;
                    this.passportPreview = URL.createObjectURL(file);
                }
            }
        },
        handleVisa(event) {
            this.visa.isValid = true;
            this.visa.msg = "";
            const file = event.target.files[0];

            // Check if a file is selected
            if (file) {
                // Check if the file is a PDF
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.visa.isValid = false;
                    this.visa.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    // Check if the file size is greater than 2MB
                    this.visa.isValid = false;
                    this.visa.msg = 'File size must be less than 2MB.';
                } else {
                    // File is valid
                    this.visa.isValid = true;
                    this.visa.msg = '';
                    this.visa.val = file;
                    this.visaPreview = URL.createObjectURL(file);
                }
            }
        },
        handleEmiratesId(event) {
            this.emiratesId.isValid = true;
            this.emiratesId.msg = "";
            const file = event.target.files[0];

            // Check if a file is selected
            if (file) {
                // Check if the file is a PDF
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.emiratesId.isValid = false;
                    this.emiratesId.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    // Check if the file size is greater than 2MB
                    this.emiratesId.isValid = false;
                    this.emiratesId.msg = 'File size must be less than 2MB.';
                } else {
                    // File is valid
                    this.emiratesId.isValid = true;
                    this.emiratesId.msg = '';
                    this.emiratesId.val = file;
                    this.emiratesIdPreview = URL.createObjectURL(file);
                }
            }
        },
        handleTradeLicense(event) {
            this.tradeLicense.isValid = true;
            this.tradeLicense.msg = "";
            const file = event.target.files[0];

            // Check if a file is selected
            if (file) {
                // Check if the file is a PDF
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.tradeLicense.isValid = false;
                    this.tradeLicense.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    // Check if the file size is greater than 2MB
                    this.tradeLicense.isValid = false;
                    this.tradeLicense.msg = 'File size must be less than 2MB.';
                } else {
                    // File is valid
                    this.tradeLicense.isValid = true;
                    this.tradeLicense.msg = '';
                    this.tradeLicense.val = file;
                    this.tradeLicensePreview = URL.createObjectURL(file);
                }
            }
        },
        handleAcademicQualificationCertificate(event) {
            this.academicQualificationCertificate.isValid = true;
            this.academicQualificationCertificate.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.academicQualificationCertificate.isValid = false;
                    this.academicQualificationCertificate.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.academicQualificationCertificate.isValid = false;
                    this.academicQualificationCertificate.msg = 'File size must be less than 2MB.';
                } else {
                    this.academicQualificationCertificate.isValid = true;
                    this.academicQualificationCertificate.msg = '';
                    this.academicQualificationCertificate.val = file;
                }
            }
        },
        handleEmploymentCard(event) {
            this.employmentCard.isValid = true;
            this.employmentCard.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.employmentCard.isValid = false;
                    this.employmentCard.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.employmentCard.isValid = false;
                    this.employmentCard.msg = 'File size must be less than 2MB.';
                } else {
                    this.employmentCard.isValid = true;
                    this.employmentCard.msg = '';
                    this.employmentCard.val = file;
                }
            }
        },
        handleCv(event) {
            this.cv.isValid = true;
            this.cv.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.cv.isValid = false;
                    this.cv.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.cv.isValid = false;
                    this.cv.msg = 'File size must be less than 2MB.';
                } else {
                    this.cv.isValid = true;
                    this.cv.msg = '';
                    this.cv.val = file;
                }
            }
        },
        handleContentPresented(event) {
            this.contentPresented.isValid = true;
            this.contentPresented.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (file.type !== 'application/pdf' && !file.type.includes('image/')) {
                    this.contentPresented.isValid = false;
                    this.contentPresented.msg = 'Only JPG, JPEG, PNG and PDF files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.contentPresented.isValid = false;
                    this.contentPresented.msg = 'File size must be less than 2MB.';
                } else {
                    this.contentPresented.isValid = true;
                    this.contentPresented.msg = '';
                    this.contentPresented.val = file;
                }
            }
        },
        async submitForm() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            // Check total file size before submission
            const totalSize = this.calculateTotalFileSize();
            const maxSize = 50 * 1024 * 1024; // 50MB in bytes

            if (totalSize > maxSize) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'File Size Too Large',
                    text: `Total file size (${this.formatFileSize(totalSize)}) exceeds the maximum allowed size of 50MB. Please reduce file sizes and try again.`,
                });
                return;
            }

            const formData = {
                initiation_token: this.$route.params.token || null,
                entity: this.entity.val,
                application_type: this.applicationType.val,
                purpose_of_request: this.purposeOfRequest.val == 'Other' ? this.otherPurposeOfRequest.val : this.purposeOfRequest.val,
                profile_picture: this.profilePicture.val,
                fullname: this.fullName.val,
                date_of_birth: this.normalizeGraphQlDate(this.dateOfBirth.val),
                place_of_birth: this.placeOfBirth.val,
                current_nationality: this.currentNationality.val,
                previous_nationality: this.previousNationality.val,
                passport_number: this.passportNumber.val,
                place_of_issue: this.placeOfIssue.val,
                date_of_issue: this.normalizeGraphQlDate(this.dateOfIssue.val),
                date_of_expiry: this.normalizeGraphQlDate(this.dateOfExpiry.val),
                marital_status: this.maritalStatus.val,
                religion: this.religion.val == 'Other' ? this.otherReligion.val : this.religion.val,
                sect: this.sect.val,
                mother_tongue: this.motherTongue.val,
                other_languages_known: this.otherLanguagesKnown.val,
                father_name: this.fatherName.val,
                father_nationality: this.fatherNationality.val,
                father_profession: this.fatherProfession.val,
                father_employer: this.fatherEmployer.val,
                father_date_of_birth: this.normalizeGraphQlDate(this.fatherDateOfBirth.val),
                father_place_of_birth: this.fatherPlaceOfBirth.val,
                mother_name: this.motherName.val,
                mother_nationality: this.motherNationality.val,
                mother_profession: this.motherProfession.val,
                mother_employer: this.motherEmployer.val,
                mother_date_of_birth: this.normalizeGraphQlDate(this.motherDateOfBirth.val),
                mother_place_of_birth: this.motherPlaceOfBirth.val,
                highest_qualification: this.highestQualification.val,
                major: this.major.val,
                institution: this.institution.val,
                academic_qualification_country: this.academicQualificationCountry.val,
                academic_qualification_year: this.academicQualificationYear.val,
                is_military_service: this.isMilitaryService.val,
                military_service_country: this.militaryServiceCountry.val,
                military_service_type_of_service: this.militaryServiceTypeOfService.val,
                military_service_rank: this.militaryServiceRank.val,
                military_service_duration: this.militaryServiceDuration.val,
                initial_port_of_entry: this.initialPortOfEntry.val,
                initial_date_of_entry: this.normalizeGraphQlDate(this.initialDateOfEntry.val),
                initial_employer: this.initialEmployer.val,
                initial_job_title: this.initialJobTitle.val,
                current_sponsor: this.currentSponsor.val,
                current_residency_number: this.currentResidencyNumber.val,
                uid_number: this.uidNumber.val,
                sponsorship_details_place_of_issue: this.sponsorshipDetailsPlaceOfIssue.val,
                sponsorship_details_date_of_issue: this.normalizeGraphQlDate(this.sponsorshipDetailsDateOfIssue.val),
                sponsorship_details_date_of_expiry: this.normalizeGraphQlDate(this.sponsorshipDetailsDateOfExpiry.val),
                employment_status: this.employmentStatus.val,
                current_employer: this.currentEmployer.val,
                current_or_last_job_title: this.currentOrLastJobTitle.val,
                employer_sector: this.employerSector.val,
                current_employment_city_or_country: this.currentEmploymentCityOrCountry.val,
                res_info_uae_proprietor: this.resInfoUaeProprietor.val,
                res_info_uae_city: this.resInfoUaeCity.val,
                res_info_uae_zone: this.resInfoUaeZone.val,
                res_info_uae_street: this.resInfoUaeStreet.val,
                res_info_uae_house: this.resInfoUaeHouse.val,
                res_info_uae_floor_no: this.resInfoUaeFloorNo.val,
                res_info_uae_tel: this.resInfoUaeTel.val,
                res_info_uae_po_box: this.resInfoUaePoBox.val,
                res_info_home_country_proprietor: this.resInfoHomeCountryProprietor.val,
                res_info_home_country_city: this.resInfoHomeCountryCity.val,
                res_info_home_country_zone: this.resInfoHomeCountryZone.val,
                res_info_home_country_street: this.resInfoHomeCountryStreet.val,
                res_info_home_country_house: this.resInfoHomeCountryHouse.val,
                res_info_home_country_floor_no: this.resInfoHomeCountryFloorNo.val,
                res_info_home_country_tel: this.resInfoHomeCountryTel.val,
                res_info_home_country_po_box: this.resInfoHomeCountryPoBox.val,
                signature: this.signature.val,
                passport: this.passport.val,
                visa: this.visa.val,
                emirates_id: this.emiratesId.val,
                trade_license: this.tradeLicense.val,
                academic_qualification_certificate: this.academicQualificationCertificate.val,
                employment_card: this.employmentCard.val,
                cv: this.cv.val,
                content_presented: this.contentPresented.val,
            }

            this.isLoading = true;

            const CREATE_MUTATION = gql`
                mutation SubmitSecurityClearance($input: CreateSubmitSecurityClearanceFormInput!) {
                    submitSecurityClearance(input: $input) {
                        error
                        error_code
                        message
                    }
                }
            `;

            let error_msg = null;
            let success_msg = null;

            try {

                await this.$apollo.mutate({
                    mutation: CREATE_MUTATION,
                    variables: {
                        input: formData
                    },
                    context: {
                        headers: {
                            'content-type': 'multipart/form-data',
                        },
                    },
                }).then((data) => {
                    if (data.data.submitSecurityClearance.error) {
                        error_msg = data.data.submitSecurityClearance.message;
                    } else {
                        success_msg = data.data.submitSecurityClearance.message;
                    }
                }).catch((error) => {
                    console.error(error)
                    // Check for specific error types
                    if (error.message && error.message.includes('PostTooLargeException')) {
                        error_msg = "The total file size is too large. Please reduce file sizes and try again.";
                    } else {
                        error_msg = error.message || "Please try again.";
                    }
                })

            } catch (error) {
                error_msg = error.message || "Please try again.";
            }

            this.isLoading = false;

            if (error_msg == null) {
                this.$swal.fire({
                    icon: 'success',
                    title: 'Congratulations!',
                    text: success_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        error_msg = null;
                        success_msg = null;
                        this.$router.replace("/");
                    }
                })
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        error_msg = null;
                        success_msg = null;
                    }
                })
            }
        },
        calculateTotalFileSize() {
            let totalSize = 0;
            const files = [
                this.profilePicture.val,
                this.signature.val,
                this.passport.val,
                this.visa.val,
                this.emiratesId.val,
                this.tradeLicense.val,
                this.academicQualificationCertificate.val,
                this.employmentCard.val,
                this.cv.val,
                this.contentPresented.val,
            ];

            files.forEach(file => {
                if (file && file.size) {
                    totalSize += file.size;
                }
            });

            return totalSize;
        },
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
    },
}
</script>

<style lang="scss" scoped>
input[type="text"],
input[type="email"],
input[type="number"],
input[type="password"],
input[type="date"],
select {
    @apply block w-full border rounded-lg py-2 px-2;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="date"]:focus,
select:focus {
    outline: none;
    border-color: #394867;
    box-shadow: 0 0 0 1px #394867;
}

.ht-42 {
    height: 42px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.form-fade-enter-active,
.form-fade-leave-active {
    transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
    opacity: 0;
    height: 0px;
}
</style>